import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PricingCard } from '@/components/billing/PricingCard';
import { PlanToggle } from '@/components/billing/PlanToggle';
import { RadiologyLiveIcon } from '@/components/icons/ProductIcons';

const FREE_FEATURES = [
  '15 reports per day',
  'Standard templates',
  'All macros + 50 custom',
  'Community support',
  'Watermarked export',
];

const SOLO_FEATURES = [
  'Unlimited reports',
  'All templates',
  'Full macro console + unlimited custom',
  'Email support (48h)',
  'HIPAA BAA included',
  'Clean report export',
];

const PRACTICE_FEATURES = [
  'Unlimited reports',
  'All templates + custom',
  'Full macro console + shared + unlimited custom',
  'Priority support (24h)',
  'HIPAA BAA included',
  'Clean report export',
  'Quarterly audit export',
];

const ENTERPRISE_FEATURES = [
  'Everything in Practice',
  'Custom + white-label templates',
  'Organization macros',
  'Dedicated CSM',
  'Custom HIPAA BAA',
  'Real-time audit API',
  'SSO / SAML',
];

export default function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <RadiologyLiveIcon size={32} />
              <span className="text-xl font-bold">
                <span className="text-muted-foreground">Radiology</span>
                <span className="text-muted-foreground">Live.</span>
                <span className="text-primary">ai</span>
              </span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Simple, transparent pricing</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free with 15 reports per day. Upgrade when you need unlimited access,
            all templates, and clean exports.
          </p>
        </div>

        <PlanToggle isAnnual={isAnnual} onToggle={setIsAnnual} className="mb-12" />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {/* Free */}
          <PricingCard
            name="Free"
            description="Get started with AI-powered reporting"
            price="$0"
            priceSubtext="forever"
            features={FREE_FEATURES}
            buttonText="Get Started"
            buttonVariant="outline"
            onSelect={() => navigate('/signup')}
          />

          {/* Solo */}
          <PricingCard
            name="Solo"
            description="For individual radiologists"
            price={isAnnual ? '$83' : '$99'}
            priceSubtext={isAnnual ? '/mo billed annually' : '/month'}
            features={SOLO_FEATURES}
            highlighted
            badge="Most Popular"
            buttonText="Start Free Trial"
            onSelect={() => navigate('/signup')}
          />

          {/* Practice */}
          <PricingCard
            name="Practice"
            description="For groups of 2-20 radiologists"
            price={isAnnual ? '$66' : '$79'}
            priceSubtext={isAnnual ? '/seat/mo billed annually' : '/seat/month'}
            features={PRACTICE_FEATURES}
            buttonText="Start Free Trial"
            onSelect={() => navigate('/signup')}
          />

          {/* Enterprise */}
          <PricingCard
            name="Enterprise"
            description="For 20+ seats with custom needs"
            price="Custom"
            priceSubtext="contact sales"
            features={ENTERPRISE_FEATURES}
            buttonText="Contact Sales"
            buttonVariant="outline"
            onSelect={() => window.location.href = 'mailto:sales@radiologylive.ai'}
          />
        </div>

        {/* FAQ/Footer note */}
        <div className="text-center mt-16">
          <p className="text-sm text-muted-foreground">
            All paid plans include a 7-day unlimited free trial. No credit card required to start.
          </p>
        </div>
      </div>
    </div>
  );
}
